"use client";

import React from "react";
import { Button, Menu, Portal, Tokens } from "@chakra-ui/react";
import GenericIcon, {
  GENERIC_ICON_CONFIG,
  type GenericIconType,
} from "./GenericIcon";

export type WithMenuProps = {
  hideMenu?: boolean;
  isLoading?: boolean;
  zIndex?: Tokens["zIndex"];
  size?: "sm" | "md";
  icon?: GenericIconType;
  trigger?: React.ReactNode;
  triggerLabel?: string;
  placement?: "left-start" | "bottom" | "bottom-start" | "bottom-end";
  minWidth?: Tokens["sizes"] | string;
  maxWidth?: Tokens["sizes"] | string;
  onOpenChange?: (details: { open: boolean }) => void;
  children?: React.ReactNode;
};

export default function WithMenu({
  hideMenu = false,
  isLoading = false,
  zIndex = "max",
  size,
  icon = "three_dots_vertical",
  trigger,
  triggerLabel: triggerLabelProp,
  placement = "left-start",
  minWidth,
  maxWidth,
  onOpenChange,
  children,
}: WithMenuProps) {
  const [mounted, setMounted] = React.useState(false);
  const triggerLabel = triggerLabelProp ?? GENERIC_ICON_CONFIG[icon].name;

  const setMountedRef = (el: HTMLButtonElement | null) => {
    if (el && !mounted) setMounted(true);
  };

  if (hideMenu) {
    return (
      <Button variant="ghost" disabled={true} size={size} visibility="hidden">
        <GenericIcon icon={icon} humanReadable={triggerLabel} />
      </Button>
    );
  }

  if (!mounted) {
    return (
      <Button variant="ghost" disabled={true} size={size} ref={setMountedRef}>
        <GenericIcon icon={icon} humanReadable={triggerLabel} />
      </Button>
    );
  }

  return (
    <Menu.Root
      positioning={{
        placement: placement,
      }}
      lazyMount={true}
      size={size}
      onOpenChange={onOpenChange}
    >
      <Menu.Trigger asChild>
        {trigger ?? (
          <Button
            variant="ghost"
            loading={isLoading}
            size={size}
            aria-label={triggerLabel}
            title={triggerLabel}
          >
            <GenericIcon icon={icon} humanReadable={triggerLabel} />
          </Button>
        )}
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner zIndex={zIndex}>
          <Menu.Content
            bg="moveon.shell.bg"
            color="moveon.surface.fg"
            borderWidth="1"
            borderColor="moveon.shell.border"
            boxShadow="lg"
            zIndex={zIndex}
            minW={minWidth}
            maxW={maxWidth}
          >
            {children}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
